import dayjs from 'dayjs';

// ================= 1. 基础辅助函数 =================

const renderTime = (time = 0) => {
    const days = Math.floor(time / 86400);
    const hours24 = Math.floor((time % 86400) / 3600);

    // 超过或等于 24 小时：显示 X天X时X分 (不显示秒)
    if (days > 0) {
        const minutes24 = Math.floor((time % 3600) / 60);
        return `${days}天${hours24}时${minutes24}分`;
    }

    // 不超过 24 小时：显示 时分秒
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = time % 60;

    if (hours === 0 && minutes === 0 && seconds === 0) {
        return `0秒`;
    }

    let result = '';
    if (hours > 0) result += `${hours}时`;
    if (minutes > 0) result += `${minutes}分`;
    if (seconds > 0) result += `${seconds}秒`;

    return result;
};

const filterType = (row) => {
    let txt = '';
    switch (row.type) {
        case 1:
            txt = '基础时长消费';
            break;
        case 2:
            txt = '超时时长消费';
            break;
        case 3:
            txt = '服务费';
            break;
        case 4:
            txt = '包间续时消费';
            break;
        case 5:
            txt = `包时消费（${row.extra?.time_package_rule?.unit_value || ''}${row.extra?.time_package_rule?.unit == 'hour' ? '小时' : '分钟'}）`;
            break;
    }
    return txt;
};

const parseDate = (dateString) => {
    if (!dateString) return 0;
    // 兼容 iOS/老版 Webview 的日期格式转换
    return new Date(dateString.replace(/-/g, '/')).getTime();
};

const calculatePauseTime = (pauseHistory) => {
    if (!pauseHistory || !pauseHistory.length) return 0;
    const totalMs = pauseHistory.reduce((total, interval) => {
        const start = parseDate(interval.start);
        const end = parseDate(interval.end);
        if (!end || !start) return total;
        return total + (end - start);
    }, 0);
    return renderTime(totalMs / 1000);
};

// ================= 2. 核心构造函数 =================
//账单模板
/**
 * 构造芯烨打印机所需的数据格式
 * @param {Object} rawData 后端返回的完整 JSON 数据
 * @param {String} printerId 打印机ID/SN
 * @param {Object} shopInfo 店铺信息
 * @param {Object} templateConfig 打印模板配置
 * @returns {Object} 供 nativePlugin.xinYe.printText 使用的参数对象
 */
export const buildPrintData58 = (rawData, printerId, shopInfo = {}, templateConfig = {}) => {
    const dataList = [];
    const mainBill = rawData.main_bill || rawData.bills?.[0]?.bill_info || {};
    const bills = rawData.bills || [];

    // 合并默认模板配置
    const config = {
        show_order_no: true,
        show_open_start_end: true,
        show_discount: true,
        show_billing_start_end: true,
        show_member_info: true,
        show_pay_time: true,
        show_windup_staff: true,
        is_sub_bill: false,
        ...templateConfig
    };

    // --- 内部快捷添加文本生成器 ---
    const addText = (text, size = 0, align = 'LEFT', bold = false) => {
        dataList.push({ type: 'text', content: text, flex: '1', size, align, bold });
    };

    const addDashedLine = (title = '') => {
        if (title) {
            if (title?.length > 2) {
                addText(`\n------------${title}------------\n`, 0, 'CENTER');
            } else {
                addText(`\n--------------${title}--------------\n`, 0, 'CENTER');
            }
        } else {
            addText(`\n--------------------------------\n`, 0, 'CENTER');
        }
    };

    // 左右对齐隐形表头键值对排版
    const addKeyValueTable = (items) => {
        if (!items || items.length === 0) return;
        const tTitles = [
            { content: items[0][0], flex: '1', size: 10, align: 'LEFT', bold: false, width: 20 },
            { content: items[0][1], flex: '1', size: 10, align: 'RIGHT', bold: false, width: 14 }
        ];
        const tRows = [];
        for (let i = 1; i < items.length; i++) {
            tRows.push({ content: items[i][0] });
            tRows.push({ content: items[i][1] });
        }
        dataList.push({ type: 'table', tableTitles: tTitles, tableRows: tRows });
    };

    // ================= 【1】 头部信息 =================
    addText(`账单类型：消费账单`);
    if (config.show_order_no && mainBill.order_no) {
        addText(`账单号：${mainBill.order_no}`);
    }
    if (config.show_open_start_end) {
        addText(`开单时间：${mainBill.checkin_at || ''}`);
        if (mainBill.state == 4) {
            addText(`结束时间：${mainBill.checkout_at || ''}`);
        }
    }

    const hasRoomData = bills.some((bill) => bill.room?.length > 0);
    const hasGoodsData = bills.some((bill) => bill.goods?.list?.some((g) => g.list?.filter((e) => e.state != 0 && e.state != 3).length > 0));
    const hasCoachData = bills.some((bill) => bill.coach?.list?.length > 0);
    const hasGiftData = bills.some((bill) => bill.gift?.list?.length > 0);
    const hasChargeData = bills.some((bill) => bill.charge?.list?.length > 0);

    // ================= 【2】 桌台费用 =================
    if (hasRoomData) {
        addDashedLine('桌台');

        // 顶部打印一次表头
        const roomHeaderTitles = [
            { content: '计费类型', flex: '1', size: 10, align: 'LEFT', bold: false, width: 12 },
            { content: '时长', flex: '1', size: 10, align: 'LEFT', bold: false, width: 8 }
        ];
        roomHeaderTitles.push({ content: '优惠', flex: '1', size: 10, align: 'LEFT', bold: false, width: 6 });
        roomHeaderTitles.push({ content: '金额', flex: '1', size: 10, align: 'LEFT', bold: false, width: 6 });

        dataList.push({ type: 'table', tableTitles: roomHeaderTitles, tableRows: [] });

        bills.forEach((bill) => {
            if (bill.room?.length) {
                const roomName = `${bill.bill_info?.room_number?.room_type?.name || ''}-${bill.bill_info?.source_room_number?.name || ''}`;
                addText(`${roomName}\n`, 0, 'LEFT', true);

                bill.room.forEach((item) => {
                    item.list
                        .filter((e) => e.state != 0)
                        .forEach((child) => {
                            const rowData = [filterType(child), renderTime(child?.extra?.use_time)];
                            rowData.push(String((Number(child.amount_original) - Number(child.amount)).toFixed(2) || 0));
                            rowData.push(String(child.amount || 0));

                            const dataTitles = [
                                { content: rowData[0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 12 },
                                { content: rowData[1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 8 }
                            ];
                            dataTitles.push({ content: rowData[2], flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 });
                            dataTitles.push({ content: rowData[rowData.length - 1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 });

                            dataList.push({ type: 'table', tableTitles: dataTitles, tableRows: [] });

                            if (config.show_billing_start_end) {
                                addText(`开始时间：${child?.extra?.start_at || ''}`);
                                if (child?.extra?.end_at) addText(`结束时间：${child?.extra?.end_at || ''}`);
                            }
                        });
                });

                if (bill.room[0]?.pause_history?.length) {
                    addText(`暂停时长：${calculatePauseTime(bill.room[0].pause_history)}`);
                }
            }
        });
    }

    // ================= 【3】 商品费用 =================
    if (hasGoodsData) {
        addDashedLine('商品');
        const tableTitles = [
            { content: '名称', flex: '1', size: 0, align: 'LEFT', bold: false, width: 8 },
            { content: '单价', flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 },
            { content: '数量', flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 }
        ];
        if (config.show_discount) tableTitles.push({ content: '优惠', flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 });
        tableTitles.push({ content: '金额', flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 });

        const tableRows = [];
        bills.forEach((bill) => {
            bill.goods?.list?.forEach((goodsGroup) => {
                goodsGroup.list
                    ?.filter((e) => e.state != 0 && e.state != 3)
                    .forEach((item) => {
                        let nameSuffix = '';
                        if (item.state == 5) nameSuffix += '(赠)';
                        if (item.extra?.online_paid) nameSuffix += '(已付)';

                        tableRows.push({ content: `${item.goods.goods_name}${nameSuffix}` });
                        tableRows.push({ content: String(item.goods?.price || 0) });
                        tableRows.push({ content: String(item.goods.num || 0) });
                        if (config.show_discount) tableRows.push({ content: String((Number(item.amount_original) - Number(item.amount)).toFixed(2) || 0) });
                        tableRows.push({ content: String(item.state == 5 ? 0 : item.amount) });
                    });
            });
        });
        if (tableRows.length > 0) dataList.push({ type: 'table', tableTitles, tableRows });
    }

    // ================= 【4】 教练费用 =================
    if (hasCoachData) {
        addDashedLine('教练');

        // 顶部打印一次表头
        const coachHeaderTitles = [
            { content: '服务', flex: '1', size: 0, align: 'LEFT', bold: false, width: 8 },
            { content: '单价', flex: '1', size: 0, align: 'LEFT', bold: false, width: 8 },
            { content: '单位', flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 }
        ];
        if (config.show_discount) coachHeaderTitles.push({ content: '优惠', flex: '1', size: 0, align: 'LEFT', bold: false, width: 5 });
        coachHeaderTitles.push({ content: '金额', flex: '1', size: 0, align: 'LEFT', bold: false, width: 5 });

        dataList.push({ type: 'table', tableTitles: coachHeaderTitles, tableRows: [] });

        bills.forEach((bill) => {
            if (!bill.coach?.list?.length) return;

            const grouped = {};
            // ?.state == 3
            bill.coach.list
                .filter((e) => e.extra)
                .forEach((item) => {
                    const goodsId = item.goods.id;
                    if (!grouped[goodsId]) grouped[goodsId] = [];
                    grouped[goodsId].push(item);
                });

            Object.values(grouped).forEach((coachGroup) => {
                const coachName = `${coachGroup[0].goods.type?.title || ''}-${coachGroup[0].goods.nickname || coachGroup[0].goods.name}`;
                addText(`${coachName}\n`, 0, 'LEFT', true);

                coachGroup.forEach((obj) => {
                    const rowData = [obj.goods.server_name || '基础'];

                    let priceTxt = '';
                    if (obj.goods.billing_type == 2) priceTxt = `${obj.goods.service?.fee_rule.fee}元/次`;
                    else if (obj.goods.billing_type == 1) priceTxt = `${obj.goods.service?.fee_rule.duration}分钟${obj.goods.service?.fee_rule.fee}元`;
                    else if (!obj.goods.service) priceTxt = `${obj.goods.fee?.duration}分钟${obj.goods.fee?.fee}元`;
                    rowData.push(priceTxt);

                    let unitTxt = obj.goods.billing_type == 2 ? `${obj.goods.service.quantity}次` : renderTime(obj?.extra?.billing_time);
                    rowData.push(unitTxt);

                    rowData.push(String((Number(obj?.amount_original) - Number(obj?.amount)).toFixed(2) || 0));
                    rowData.push(String(obj?.amount || 0));

                    const dataTitles = [
                        { content: rowData[0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 8 },
                        { content: rowData[1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 8 },
                        { content: rowData[2], flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 }
                    ];
                    if (config.show_discount) dataTitles.push({ content: rowData[3], flex: '1', size: 0, align: 'LEFT', bold: false, width: 5 });
                    dataTitles.push({ content: rowData[rowData.length - 1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 5 });

                    dataList.push({ type: 'table', tableTitles: dataTitles, tableRows: [] });

                    if (obj.goods.billing_type != 2) {
                        if (config.show_billing_start_end) {
                            addText(`开始时间：${obj?.extra?.start_at || ''}`);
                            if (obj?.extra?.end_at) addText(`结束时间：${obj?.extra?.end_at || ''}`);
                        }
                        if (obj.pause_history) addText(`暂停时长：${calculatePauseTime(obj.pause_history)}`);
                    }
                });
            });
        });
    }

    // ================= 【5】 礼物模块 =================
    if (hasGiftData && !config.is_sub_bill) {
        addDashedLine('礼物');

        // 顶部打印一次表头
        const giftHeaderTitles = [
            { content: '教练名称', flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 },
            { content: '礼物名称', flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 }
        ];
        giftHeaderTitles.push({ content: '优惠', flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 });
        giftHeaderTitles.push({ content: '金额', flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 });

        dataList.push({ type: 'table', tableTitles: giftHeaderTitles, tableRows: [] });

        bills.forEach((bill) => {
            bill.gift?.list?.forEach((item) => {
                const rowData = [item.coach?.name || '--', item.gift?.name || '--'];
                rowData.push(String((Number(item.amount_original) - Number(item.amount)).toFixed(2) || 0));
                rowData.push(String(item.amount || 0));

                const dataTitles = [
                    { content: rowData[0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 },
                    { content: rowData[1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 }
                ];
                if (config.show_discount) dataTitles.push({ content: rowData[2], flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 });
                dataTitles.push({ content: rowData[rowData.length - 1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 });

                dataList.push({ type: 'table', tableTitles: dataTitles, tableRows: [] });
            });
        });
    }

    // ================= 【6】 充电模块 =================
    if (hasChargeData && !config.is_sub_bill) {
        addDashedLine('充电');

        // 顶部打印一次表头
        const chargeHeaderTitles = [
            { content: '充电口', flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 },
            { content: '计费时长', flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 }
        ];
        chargeHeaderTitles.push({ content: '优惠', flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 });
        chargeHeaderTitles.push({ content: '金额', flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 });

        dataList.push({ type: 'table', tableTitles: chargeHeaderTitles, tableRows: [] });

        bills.forEach((bill) => {
            bill.charge?.list?.forEach((item) => {
                const portName = item.extra?.position == 1 ? '左侧充电口' : '右侧充电口';

                const rowData = [portName, renderTime(item?.extra?.use_time)];
                rowData.push(String((Number(item.amount_original) - Number(item.amount)).toFixed(2) || 0));
                rowData.push(String(item.amount || 0));

                const dataTitles = [
                    { content: rowData[0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 },
                    { content: rowData[1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 }
                ];
                dataTitles.push({ content: rowData[2], flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 });
                dataTitles.push({ content: rowData[rowData.length - 1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 6 });

                dataList.push({ type: 'table', tableTitles: dataTitles, tableRows: [] });

                if (config.show_billing_start_end) {
                    addText(`开始时间：${item?.extra?.start_at || ''}`);
                    if (item?.extra?.end_at) addText(`结束时间：${item?.extra?.end_at || ''}`);
                }
            });
        });
    }

    // ================= 【7】 收支总计 (左右两端对齐排版) =================
    addDashedLine();
    const summary = {
        total: rawData.total_amount || '0',
        discount: rawData.discount_amount || rawData.amount_sub || '0',
        room: rawData.room?.total_amount || '0',
        goods: rawData.goods?.total_amount || '0',
        coach: rawData.coach?.total_amount || '0',
        gift: rawData.gift?.total_amount || '0',
        charge: rawData.charge?.total_amount || '0'
    };

    const summaryItems = [];
    if (hasRoomData) summaryItems.push(['台费合计:', `￥${summary.room}`]);
    if (hasGoodsData) summaryItems.push(['商品合计:', `￥${summary.goods}`]);
    if (hasCoachData) summaryItems.push(['教练合计:', `￥${summary.coach}`]);
    if (hasGiftData && !config.is_sub_bill) summaryItems.push(['礼物合计:', `￥${summary.gift}`]);
    if (hasChargeData && !config.is_sub_bill) summaryItems.push(['充电合计:', `￥${summary.charge}`]);

    summaryItems.push(['账单总额:', `￥${rawData.original_amount}`]);
    if (config.show_discount) summaryItems.push(['优惠总额:', `￥${summary.discount}`]);

    addKeyValueTable(summaryItems);

    // ================= 【8】 支付信息 (左右两端对齐排版) =================
    addDashedLine('支付信息');
    const payment = rawData?.advance_payment?.payment_group_list || {};

    const payItems = [];
    if (payment.balance?.list?.length > 0 && Number(payment.balance?.total_amount) > 0) payItems.push(['本金支付:', `￥${payment.balance?.total_amount || '0.00'}`]);
    if (payment.bonus?.list?.length > 0 && Number(payment.bonus?.total_amount) > 0) payItems.push(['赠金支付:', `￥${payment.bonus?.total_amount || '0.00'}`]);
    if (payment.group_buy?.list?.length > 0 && Number(payment.group_buy?.total_amount) > 0) payItems.push(['团购:', `￥${payment.group_buy?.total_amount || '0.00'}`]);
    if (payment.online_pay?.list?.length > 0 && Number(payment.online_pay?.total_amount) > 0) payItems.push(['聚合支付:', `￥${payment.online_pay?.total_amount || '0.00'}`]);
    if (payment.cash?.list?.length > 0 && Number(payment.cash?.total_amount) > 0) payItems.push(['现金支付:', `￥${payment.cash?.total_amount || '0.00'}`]);

    addKeyValueTable(payItems);
    addText(`合计：￥${rawData?.advance_payment?.total_amount || '0.00'}`, 0, 'RIGHT', false);
    if (Number(rawData.unpaid_amount) !== 0) {
        addText(`\n应付款：￥${rawData.unpaid_amount}\n`, 1, 'CENTER', true);
    }

    // ================= 【9】 会员信息 (左右两端对齐排版) =================
    if (config.show_member_info && mainBill?.customer_phone && mainBill?.customer_name) {
        addDashedLine('会员信息');

        const memberItems = [
            ['会员手机号:', mainBill?.customer_phone],
            ['会员姓名:', mainBill?.customer_name || '--'],
            ['剩余本金:', `￥${mainBill?.member_info?.member_info?.balance || '0.00'}`],
            ['剩余赠金:', `￥${mainBill?.member_info?.member_info?.bonus_account || '0.00'}`]
            // ['剩余积分:', mainBill?.score || '0']
        ];
        addKeyValueTable(memberItems);
    }

    // ================= 【10】 底部落款 =================
    addDashedLine();
    if (config.show_pay_time) {
        addText(`打印时间：${dayjs().format('YYYY-MM-DD HH:mm:ss')}`);
    }
    if (mainBill?.op_user?.name) {
        addText(`收银员：${mainBill?.op_user?.name || '--'}`);
    }

    addText(`联系电话：${shopInfo.phone || shopInfo.tel || '--'}`);
    const addressStr = typeof shopInfo.address === 'object' ? shopInfo.address.full_text || shopInfo.address.detailed : shopInfo.address;
    addText(`地址：${addressStr || '--'}\n\n\n\n`);

    return {
        titleInfo: {
            title: shopInfo.name || '店铺名称',
            size: 20
        },
        printerId: printerId,
        data: dataList
    };
};

//80
export const buildPrintData80 = (rawData, printerId, shopInfo = {}, templateConfig = {}) => {
    const dataList = [];
    const mainBill = rawData.main_bill || rawData.bills?.[0]?.bill_info || {};
    const bills = rawData.bills || [];

    // 合并默认模板配置
    const config = {
        show_order_no: true,
        show_open_start_end: true,
        show_discount: true,
        show_billing_start_end: true,
        show_member_info: true,
        show_pay_time: true,
        show_windup_staff: true,
        is_sub_bill: false,
        ...templateConfig
    };

    // --- 内部快捷添加文本生成器 ---
    const addText = (text, size = 0, align = 'LEFT', bold = false) => {
        dataList.push({ type: 'text', content: text, flex: '1', size, align, bold });
    };

    const addDashedLine = (title = '') => {
        if (title) {
            if (title?.length > 2) {
                addText(`\n------------------${title}------------------\n`, 0, 'CENTER');
            } else {
                addText(`\n---------------------${title}---------------------\n`, 0, 'CENTER');
            }
        } else {
            addText(`\n--------------------------------------------\n`, 0, 'CENTER');
        }
    };

    // 左右对齐隐形表头键值对排版
    const addKeyValueTable = (items) => {
        if (!items || items.length === 0) return;
        const tTitles = [
            { content: items[0][0], flex: '1', size: 10, align: 'LEFT', bold: false, width: 35 },
            { content: items[0][1], flex: '1', size: 10, align: 'RIGHT', bold: false, width: 20 }
        ];
        const tRows = [];
        for (let i = 1; i < items.length; i++) {
            tRows.push({ content: items[i][0] });
            tRows.push({ content: items[i][1] });
        }
        dataList.push({ type: 'table', tableTitles: tTitles, tableRows: tRows });
    };

    // ================= 【1】 头部信息 =================
    addText(`账单类型：消费账单`);
    if (config.show_order_no && mainBill.order_no) {
        addText(`账单号：${mainBill.order_no}`);
    }
    if (config.show_open_start_end) {
        addText(`开单时间：${mainBill.checkin_at || ''}`);
        if (mainBill.state == 4) {
            addText(`结束时间：${mainBill.checkout_at || ''}`);
        }
    }

    const hasRoomData = bills.some((bill) => bill.room?.length > 0);
    const hasGoodsData = bills.some((bill) => bill.goods?.list?.some((g) => g.list?.filter((e) => e.state != 0 && e.state != 3).length > 0));
    const hasCoachData = bills.some((bill) => bill.coach?.list?.length > 0);
    const hasGiftData = bills.some((bill) => bill.gift?.list?.length > 0);
    const hasChargeData = bills.some((bill) => bill.charge?.list?.length > 0);

    // ================= 【2】 桌台费用 =================
    if (hasRoomData) {
        addDashedLine('桌台');

        // 顶部打印一次表头
        const roomHeaderTitles = [
            { content: '计费类型', flex: '1', size: 10, align: 'LEFT', bold: false, width: 15 },
            { content: '时长', flex: '1', size: 10, align: 'LEFT', bold: false, width: 12 }
        ];
        roomHeaderTitles.push({ content: '优惠', flex: '1', size: 10, align: 'LEFT', bold: false, width: 10 });
        roomHeaderTitles.push({ content: '金额', flex: '1', size: 10, align: 'LEFT', bold: false, width: 12 });

        dataList.push({ type: 'table', tableTitles: roomHeaderTitles, tableRows: [] });

        bills.forEach((bill) => {
            if (bill.room?.length) {
                const roomName = `${bill.bill_info?.room_number?.room_type?.name || ''}-${bill.bill_info?.source_room_number?.name || ''}`;
                addText(`${roomName}\n`, 0, 'LEFT', true);

                bill.room.forEach((item) => {
                    item.list
                        .filter((e) => e.state != 0)
                        .forEach((child) => {
                            const rowData = [filterType(child), renderTime(child?.extra?.use_time)];
                            rowData.push(String((Number(child.amount_original) - Number(child.amount)).toFixed(2) || 0));
                            rowData.push(String(child.amount || 0));

                            const dataTitles = [
                                { content: rowData[0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 15 },
                                { content: rowData[1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 12 }
                            ];
                            dataTitles.push({ content: rowData[2], flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 });
                            dataTitles.push({ content: rowData[rowData.length - 1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 12 });

                            dataList.push({ type: 'table', tableTitles: dataTitles, tableRows: [] });

                            if (config.show_billing_start_end) {
                                addText(`开始时间：${child?.extra?.start_at || ''}`);
                                if (child?.extra?.end_at) addText(`结束时间：${child?.extra?.end_at || ''}`);
                            }
                        });
                });

                if (bill.room[0]?.pause_history?.length) {
                    addText(`暂停时长：${calculatePauseTime(bill.room[0].pause_history)}`);
                }
            }
        });
    }

    // ================= 【3】 商品费用 =================
    if (hasGoodsData) {
        addDashedLine('商品');
        const tableTitles = [
            { content: '名称', flex: '1', size: 0, align: 'LEFT', bold: false, width: 14 },
            { content: '单价', flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 },
            { content: '数量', flex: '1', size: 0, align: 'LEFT', bold: false, width: 8 }
        ];
        if (config.show_discount) tableTitles.push({ content: '优惠', flex: '1', size: 0, align: 'LEFT', bold: false, width: 8 });
        tableTitles.push({ content: '金额', flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 });

        const tableRows = [];
        bills.forEach((bill) => {
            bill.goods?.list?.forEach((goodsGroup) => {
                goodsGroup.list
                    ?.filter((e) => e.state != 0 && e.state != 3)
                    .forEach((item) => {
                        let nameSuffix = '';
                        if (item.state == 5) nameSuffix += '(赠)';
                        // if (item.extra?.online_paid) nameSuffix += '(已付)';

                        tableRows.push({ content: `${item.goods.goods_name}${nameSuffix}` });
                        tableRows.push({ content: String(item?.goods?.price || 0) });
                        tableRows.push({ content: String(item?.goods.num || 0) });
                        if (config.show_discount) tableRows.push({ content: String((Number(item.amount_original) - Number(item.amount)).toFixed(2) || 0) });
                        tableRows.push({ content: String(item.state == 5 ? 0 : item.amount) });
                    });
            });
        });
        if (tableRows.length > 0) dataList.push({ type: 'table', tableTitles, tableRows });
    }

    // ================= 【4】 教练费用 =================
    if (hasCoachData) {
        addDashedLine('教练');

        // 顶部打印一次表头
        const coachHeaderTitles = [
            { content: '服务', flex: '1', size: 0, align: 'LEFT', bold: false, width: 14 },
            { content: '单价', flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 },
            { content: '单位', flex: '1', size: 0, align: 'LEFT', bold: false, width: 8 }
        ];
        if (config.show_discount) coachHeaderTitles.push({ content: '优惠', flex: '1', size: 0, align: 'LEFT', bold: false, width: 8 });
        coachHeaderTitles.push({ content: '金额', flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 });

        dataList.push({ type: 'table', tableTitles: coachHeaderTitles, tableRows: [] });

        bills.forEach((bill) => {
            if (!bill.coach?.list?.length) return;

            const grouped = {};
            // ?.state == 3
            bill.coach.list
                .filter((e) => e.extra)
                .forEach((item) => {
                    const goodsId = item.goods.id;
                    if (!grouped[goodsId]) grouped[goodsId] = [];
                    grouped[goodsId].push(item);
                });

            Object.values(grouped).forEach((coachGroup) => {
                const coachName = `${coachGroup[0].goods.type?.title || ''}-${coachGroup[0].goods.nickname || coachGroup[0].goods.name}`;
                addText(`${coachName}\n`, 0, 'LEFT', true);

                coachGroup.forEach((obj) => {
                    const rowData = [obj.goods.server_name || '基础'];

                    let priceTxt = '';
                    if (obj.goods.billing_type == 2) priceTxt = `${obj.goods.service?.fee_rule.fee}元/次`;
                    else if (obj.goods.billing_type == 1) priceTxt = `${obj.goods.service?.fee_rule.duration}分钟${obj.goods.service?.fee_rule.fee}元`;
                    else if (!obj.goods.service) priceTxt = `${obj.goods.fee?.duration}分钟${obj.goods.fee?.fee}元`;
                    rowData.push(priceTxt);

                    let unitTxt = obj.goods.billing_type == 2 ? `${obj.goods.service.quantity}次` : renderTime(obj?.extra?.billing_time);
                    rowData.push(unitTxt);

                    rowData.push(String((Number(obj?.amount_original) - Number(obj?.amount)).toFixed(2) || 0));
                    rowData.push(String(obj?.amount || 0));

                    const dataTitles = [
                        { content: rowData[0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 },
                        { content: rowData[1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 },
                        { content: rowData[2], flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 }
                    ];
                    dataTitles.push({ content: rowData[3], flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 });
                    dataTitles.push({ content: rowData[rowData.length - 1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 });

                    dataList.push({ type: 'table', tableTitles: dataTitles, tableRows: [] });

                    if (obj.goods.billing_type != 2) {
                        if (config.show_billing_start_end) {
                            addText(`开始时间：${obj?.extra?.start_at || ''}`);
                            if (obj?.extra?.end_at) addText(`结束时间：${obj?.extra?.end_at || ''}`);
                        }
                        if (obj.pause_history) addText(`暂停时长：${calculatePauseTime(obj.pause_history)}`);
                    }
                });
            });
        });
    }

    // ================= 【5】 礼物模块 =================
    if (hasGiftData && !config.is_sub_bill) {
        addDashedLine('礼物');

        // 顶部打印一次表头
        const giftHeaderTitles = [
            { content: '教练名称', flex: '1', size: 0, align: 'LEFT', bold: false, width: 15 },
            { content: '礼物名称', flex: '1', size: 0, align: 'LEFT', bold: false, width: 15 }
        ];
        giftHeaderTitles.push({ content: '优惠', flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 });
        giftHeaderTitles.push({ content: '金额', flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 });

        dataList.push({ type: 'table', tableTitles: giftHeaderTitles, tableRows: [] });

        bills.forEach((bill) => {
            bill.gift?.list?.forEach((item) => {
                const rowData = [item.coach?.name || '--', item.gift?.name || '--'];
                rowData.push(String((Number(item.amount_original) - Number(item.amount)).toFixed(2) || 0));
                rowData.push(String(item.amount || 0));

                const dataTitles = [
                    { content: rowData[0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 15 },
                    { content: rowData[1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 15 }
                ];
                if (config.show_discount) dataTitles.push({ content: rowData[2], flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 });
                dataTitles.push({ content: rowData[rowData.length - 1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 });

                dataList.push({ type: 'table', tableTitles: dataTitles, tableRows: [] });
            });
        });
    }

    // ================= 【6】 充电模块 =================
    if (hasChargeData && !config.is_sub_bill) {
        addDashedLine('充电');

        // 顶部打印一次表头
        const chargeHeaderTitles = [
            { content: '充电口', flex: '1', size: 0, align: 'LEFT', bold: false, width: 15 },
            { content: '计费时长', flex: '1', size: 0, align: 'LEFT', bold: false, width: 15 }
        ];
        chargeHeaderTitles.push({ content: '优惠', flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 });
        chargeHeaderTitles.push({ content: '金额', flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 });

        dataList.push({ type: 'table', tableTitles: chargeHeaderTitles, tableRows: [] });

        bills.forEach((bill) => {
            bill.charge?.list?.forEach((item) => {
                const portName = item.extra?.position == 1 ? '左侧充电口' : '右侧充电口';

                const rowData = [portName, renderTime(item?.extra?.use_time)];
                rowData.push(String((Number(item.amount_original) - Number(item.amount)).toFixed(2) || 0));
                rowData.push(String(item.amount || 0));

                const dataTitles = [
                    { content: rowData[0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 15 },
                    { content: rowData[1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 15 }
                ];
                if (config.show_discount) dataTitles.push({ content: rowData[2], flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 });
                dataTitles.push({ content: rowData[rowData.length - 1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 10 });

                dataList.push({ type: 'table', tableTitles: dataTitles, tableRows: [] });

                if (config.show_billing_start_end) {
                    addText(`开始时间：${item?.extra?.start_at || ''}`);
                    if (item?.extra?.end_at) addText(`结束时间：${item?.extra?.end_at || ''}`);
                }
            });
        });
    }

    // ================= 【7】 收支总计 (左右两端对齐排版) =================
    addDashedLine();
    const summary = {
        total: rawData.total_amount || '0',
        discount: rawData.discount_amount || rawData.amount_sub || '0',
        room: rawData.room?.total_amount || '0',
        goods: rawData.goods?.total_amount || '0',
        coach: rawData.coach?.total_amount || '0',
        gift: rawData.gift?.total_amount || '0',
        charge: rawData.charge?.total_amount || '0'
    };

    const summaryItems = [];
    if (hasRoomData) summaryItems.push(['台费合计:', `￥${summary.room}`]);
    if (hasGoodsData) summaryItems.push(['商品合计:', `￥${summary.goods}`]);
    if (hasCoachData) summaryItems.push(['教练合计:', `￥${summary.coach}`]);
    if (hasGiftData && !config.is_sub_bill) summaryItems.push(['礼物合计:', `￥${summary.gift}`]);
    if (hasChargeData && !config.is_sub_bill) summaryItems.push(['充电合计:', `￥${summary.charge}`]);

    summaryItems.push(['账单总额:', `￥${rawData.original_amount}`]);
    if (config.show_discount) summaryItems.push(['优惠总额:', `￥${summary.discount}`]);

    addKeyValueTable(summaryItems);

    // ================= 【8】 支付信息 (左右两端对齐排版) =================
    addDashedLine('支付信息');
    const payment = rawData?.advance_payment?.payment_group_list || {};

    const payItems = [];
    if (payment.balance?.list?.length > 0 && Number(payment.balance?.total_amount) > 0) payItems.push(['本金支付:', `￥${payment.balance?.total_amount || '0.00'}`]);
    if (payment.bonus?.list?.length > 0 && Number(payment.bonus?.total_amount) > 0) payItems.push(['赠金支付:', `￥${payment.bonus?.total_amount || '0.00'}`]);
    if (payment.group_buy?.list?.length > 0 && Number(payment.group_buy?.total_amount) > 0) payItems.push(['团购:', `￥${payment.group_buy?.total_amount || '0.00'}`]);
    if (payment.online_pay?.list?.length > 0 && Number(payment.online_pay?.total_amount) > 0) payItems.push(['聚合支付:', `￥${payment.online_pay?.total_amount || '0.00'}`]);
    if (payment.cash?.list?.length > 0 && Number(payment.cash?.total_amount) > 0) payItems.push(['现金支付:', `￥${payment.cash?.total_amount || '0.00'}`]);

    addKeyValueTable(payItems);
    addText(`合计：￥${rawData?.advance_payment?.total_amount || '0.00'}`, 0, 'RIGHT', false);
    if (Number(rawData.unpaid_amount) !== 0) {
        addText(`\n应付款：￥${rawData.unpaid_amount}\n`, 1, 'CENTER', true);
    }

    // ================= 【9】 会员信息 (左右两端对齐排版) =================
    if (mainBill?.customer_phone && mainBill?.customer_name) {
        addDashedLine('会员信息');

        const memberItems = [
            ['会员手机号:', mainBill?.customer_phone],
            ['会员姓名:', mainBill?.customer_name || '--'],
            ['剩余本金:', `￥${mainBill?.member_info?.member_info?.balance || '0.00'}`],
            ['剩余赠金:', `￥${mainBill?.member_info?.member_info?.bonus_account || '0.00'}`]
            // ['剩余积分:', mainBill?.score || '0']
        ];
        addKeyValueTable(memberItems);
    }

    // ================= 【10】 底部落款 =================
    addDashedLine();
    if (config.show_pay_time) {
        addText(`打印时间：${dayjs().format('YYYY-MM-DD HH:mm:ss')}`);
    }
    if (mainBill?.op_user?.name) {
        addText(`收银员：${mainBill?.op_user?.name || '--'}`);
    }

    addText(`联系电话：${shopInfo.phone || shopInfo.tel || '--'}`);
    const addressStr = typeof shopInfo.address === 'object' ? shopInfo.address.full_text || shopInfo.address.detailed : shopInfo.address;
    addText(`地址：${addressStr || '--'}\n\n\n\n`);

    return {
        titleInfo: {
            title: shopInfo.name || '店铺名称',
            size: 20
        },
        printerId: printerId,
        data: dataList
    };
};

//充值模板

/**
 * 构造芯烨打印机所需的数据格式 (充值账单专用)
 * @param {Object} rawData 后端返回的充值详情数据 (对应 topUpInfo.vue 中的 expendDetail)
 * @param {String} printerId 打印机ID/SN
 * @param {Object} shopInfo 店铺信息 { name: '店铺名', tel: '电话', address: '地址' }
 * @param {Object} templateConfig 打印模板配置
 * @returns {Object} 供 nativePlugin.xinYe.printText 使用的参数对象
 */
export const buildTopUpPrintData58 = (rawData, printerId, shopInfo = {}, templateConfig = {}) => {
    const dataList = [];

    // 合并默认模板配置
    const config = {
        show_order_no: true,
        show_member_info: true,
        show_pay_time: true,
        show_windup_staff: true,
        ...templateConfig
    };

    // --- 内部快捷辅助生成器 ---
    const addText = (text, size = 0, align = 'LEFT', bold = false) => {
        dataList.push({ type: 'text', content: text, flex: '1', size, align, bold });
    };

    const addDashedLine = (title = '') => {
        if (title) {
            if (title?.length > 2) {
                addText(`\n------------${title}------------\n`, 0, 'CENTER');
            } else {
                addText(`\n--------------${title}--------------\n`, 0, 'CENTER');
            }
        } else {
            addText(`\n--------------------------------\n`, 0, 'CENTER');
        }
    };

    // 左右对齐隐形表头键值对排版
    const addKeyValueTable = (items) => {
        if (!items || items.length === 0) return;
        // 隐形表头：必须写明 width，保证芯烨 SDK 正常渲染并精准两端对齐
        const tTitles = [
            { content: items[0][0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 18 },
            { content: items[0][1], flex: '1', size: 0, align: 'RIGHT', bold: false, width: 20 }
        ];
        const tRows = [];
        for (let i = 1; i < items.length; i++) {
            tRows.push({ content: items[i][0] });
            tRows.push({ content: items[i][1] });
        }
        dataList.push({ type: 'table', tableTitles: tTitles, tableRows: tRows });
    };

    // ================= 【1】 头部信息 =================
    addText(`账单类型：充值账单`);
    if (config.show_order_no && rawData.order_no) {
        addText(`账单号：${rawData.order_no}`);
    }

    // ================= 【2】 充值信息 =================
    addDashedLine('充值信息');

    const topUpItems = [];

    // 参与活动名称 (可能存在多个)
    if (rawData.activity_info && rawData.activity_info.length > 0) {
        rawData.activity_info.forEach((item) => {
            topUpItems.push(['参与活动名称:', item.name || '--']);
        });
    }

    // 本金与赠金计算
    const balanceChange = Number(rawData.change_balance_info?.change || 0);
    const bonusChange = Number(rawData.change_bonus_info?.change || 0);
    const actualReceived = (balanceChange + bonusChange).toFixed(2);

    topUpItems.push(['会员本金:', `￥${balanceChange}`]);
    topUpItems.push(['会员赠金:', `￥${bonusChange}`]);
    topUpItems.push(['实际到账:', `￥${actualReceived}`]);

    addKeyValueTable(topUpItems);

    // ================= 【3】 会员信息 =================
    if (config.show_member_info && rawData.member) {
        addDashedLine('会员信息');

        const member = rawData.member;
        const phoneMasked = member.phone ? member.phone.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2') : '--';

        const memberItems = [
            ['会员手机号', phoneMasked],
            ['会员姓名', member.nickname || '--']
        ];

        // 积分与经验值 (如后端无该字段，做判空保护)
        if (rawData.change_points_info?.change) {
            memberItems.push(['赠送积分', String(rawData.change_points_info.change)]);
        }
        if (rawData.change_experience_info?.change) {
            memberItems.push(['获得经验值', String(rawData.change_experience_info.change)]);
        }

        // 资金变动明细
        memberItems.push(['变动前本金', `￥${Number(rawData.change_balance_info?.before || 0).toFixed(2)}`]);
        memberItems.push(['变动后本金', `￥${Number(rawData.change_balance_info?.after || 0).toFixed(2)}`]);
        memberItems.push(['变动前赠金', `￥${Number(rawData.change_bonus_info?.before || 0).toFixed(2)}`]);
        memberItems.push(['变动后赠金', `￥${Number(rawData.change_bonus_info?.after || 0).toFixed(2)}`]);

        addKeyValueTable(memberItems);
    }

    // ================= 【4】 支付信息 =================
    addDashedLine('支付信息');

    const payList = rawData.pay_list || {};
    const payItems = [];

    if (payList.cash && Number(payList.cash) > 0) {
        payItems.push(['现金支付', `￥${Number(payList.cash).toFixed(2)}`]);
    }
    if (payList.online_pay && Number(payList.online_pay) > 0) {
        payItems.push(['聚合支付', `￥${Number(payList.online_pay).toFixed(2)}`]);
    }
    if (rawData.refund_total && Number(rawData.refund_total) > 0) {
        payItems.push(['已退款', `-￥${Number(rawData.refund_total).toFixed(2)}`]);
    }

    // 合计收款
    // payItems.push(['', `合计：￥${Number(rawData.amount || 0).toFixed(2)}`]);
    addKeyValueTable(payItems);
    addText(`合计：￥${Number(rawData.amount || 0).toFixed(2)}`, 0, 'RIGHT', false);
    // ================= 【5】 底部落款 =================
    addDashedLine();
    if (config.show_pay_time) {
        // 如果后端传了支付时间以传入为主，否则取当前打印时间
        const payTime = rawData.pay_time || dayjs().format('YYYY-MM-DD HH:mm:ss');
        addText(`打印时间：${payTime}`);
    }
    if (rawData.order_staff_name || rawData.op_user?.name) {
        // 根据实际返回的操作员字段名进行替换
        addText(`收银员：${rawData.order_staff_name || rawData.op_user?.name || '--'}`);
    }

    // 安全获取电话和地址属性，防止抛错
    addText(`联系电话：${shopInfo.phone || shopInfo.tel || '--'}`);
    const addressStr = typeof shopInfo.address === 'object' ? shopInfo.address.detailed || shopInfo.address.full_text : shopInfo.address;
    addText(`地址：${addressStr || '--'}\n\n\n\n`);

    // 最终返回符合 SDK 要求的对象
    return {
        titleInfo: {
            title: shopInfo.name || '店铺名称',
            size: 20
        },
        printerId: printerId,
        data: dataList
    };
};

export const buildTopUpPrintData80 = (rawData, printerId, shopInfo = {}, templateConfig = {}) => {
    const dataList = [];

    // 合并默认模板配置
    const config = {
        show_order_no: true,
        show_member_info: true,
        show_pay_time: true,
        show_windup_staff: true,
        ...templateConfig
    };

    // --- 内部快捷辅助生成器 ---
    const addText = (text, size = 0, align = 'LEFT', bold = false) => {
        dataList.push({ type: 'text', content: text, flex: '1', size, align, bold });
    };

    const addDashedLine = (title = '') => {
        if (title) {
            if (title?.length > 2) {
                addText(`\n------------------${title}------------------\n`, 0, 'CENTER');
            } else {
                addText(`\n---------------------${title}---------------------\n`, 0, 'CENTER');
            }
        } else {
            addText(`\n--------------------------------------------\n`, 0, 'CENTER');
        }
    };

    // 左右对齐隐形表头键值对排版
    const addKeyValueTable = (items) => {
        if (!items || items.length === 0) return;
        // 隐形表头：必须写明 width，保证芯烨 SDK 正常渲染并精准两端对齐
        const tTitles = [
            { content: items[0][0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 35 },
            { content: items[0][1], flex: '1', size: 0, align: 'RIGHT', bold: false, width: 28 }
        ];
        const tRows = [];
        for (let i = 1; i < items.length; i++) {
            tRows.push({ content: items[i][0] });
            tRows.push({ content: items[i][1] });
        }
        dataList.push({ type: 'table', tableTitles: tTitles, tableRows: tRows });
    };

    // ================= 【1】 头部信息 =================
    addText(`账单类型：充值账单`);
    if (config.show_order_no && rawData.order_no) {
        addText(`账单号：${rawData.order_no}`);
    }

    // ================= 【2】 充值信息 =================
    addDashedLine('充值信息');

    const topUpItems = [];

    // 参与活动名称 (可能存在多个)
    if (rawData.activity_info && rawData.activity_info.length > 0) {
        rawData.activity_info.forEach((item) => {
            topUpItems.push(['参与活动名称:', item.name || '--']);
        });
    }

    // 本金与赠金计算
    const balanceChange = Number(rawData.change_balance_info?.change || 0);
    const bonusChange = Number(rawData.change_bonus_info?.change || 0);
    const actualReceived = (balanceChange + bonusChange).toFixed(2);

    topUpItems.push(['会员本金:', `￥${balanceChange}`]);
    topUpItems.push(['会员赠金:', `￥${bonusChange}`]);
    topUpItems.push(['实际到账:', `￥${actualReceived}`]);

    addKeyValueTable(topUpItems);

    // ================= 【3】 会员信息 =================
    if (config.show_member_info && rawData.member) {
        addDashedLine('会员信息');

        const member = rawData.member;
        const phoneMasked = member.phone ? member.phone.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2') : '--';

        const memberItems = [
            ['会员手机号', phoneMasked],
            ['会员姓名', member.nickname || '--']
        ];

        // 积分与经验值 (如后端无该字段，做判空保护)
        if (rawData.change_points_info?.change) {
            memberItems.push(['赠送积分', String(rawData.change_points_info.change)]);
        }
        if (rawData.change_experience_info?.change) {
            memberItems.push(['获得经验值', String(rawData.change_experience_info.change)]);
        }

        // 资金变动明细
        memberItems.push(['变动前本金', `￥${Number(rawData.change_balance_info?.before || 0).toFixed(2)}`]);
        memberItems.push(['变动后本金', `￥${Number(rawData.change_balance_info?.after || 0).toFixed(2)}`]);
        memberItems.push(['变动前赠金', `￥${Number(rawData.change_bonus_info?.before || 0).toFixed(2)}`]);
        memberItems.push(['变动后赠金', `￥${Number(rawData.change_bonus_info?.after || 0).toFixed(2)}`]);

        addKeyValueTable(memberItems);
    }

    // ================= 【4】 支付信息 =================
    addDashedLine('支付信息');

    const payList = rawData.pay_list || {};
    const payItems = [];

    if (payList.cash && Number(payList.cash) > 0) {
        payItems.push(['现金支付', `￥${Number(payList.cash).toFixed(2)}`]);
    }
    if (payList.online_pay && Number(payList.online_pay) > 0) {
        payItems.push(['聚合支付', `￥${Number(payList.online_pay).toFixed(2)}`]);
    }
    if (rawData.refund_total && Number(rawData.refund_total) > 0) {
        payItems.push(['已退款', `-￥${Number(rawData.refund_total).toFixed(2)}`]);
    }

    // 合计收款
    // payItems.push(['', `合计：￥${Number(rawData.amount || 0).toFixed(2)}`]);
    addKeyValueTable(payItems);
    addText(`合计：￥${Number(rawData.amount || 0).toFixed(2)}`, 0, 'RIGHT', false);
    // ================= 【5】 底部落款 =================
    addDashedLine();
    if (config.show_pay_time) {
        // 如果后端传了支付时间以传入为主，否则取当前打印时间
        const payTime = rawData.pay_time || dayjs().format('YYYY-MM-DD HH:mm:ss');
        addText(`打印时间：${payTime}`);
    }
    if (rawData.order_staff_name || rawData.op_user?.name) {
        // 根据实际返回的操作员字段名进行替换
        addText(`收银员：${rawData.order_staff_name || rawData.op_user?.name || '--'}`);
    }

    // 安全获取电话和地址属性，防止抛错
    addText(`联系电话：${shopInfo.phone || shopInfo.tel || '--'}`);
    const addressStr = typeof shopInfo.address === 'object' ? shopInfo.address.detailed || shopInfo.address.full_text : shopInfo.address;
    addText(`地址：${addressStr || '--'}\n\n\n\n`);

    // 最终返回符合 SDK 要求的对象
    return {
        titleInfo: {
            title: shopInfo.name || '店铺名称',
            size: 20
        },
        printerId: printerId,
        data: dataList
    };
};

//交接班小票
/**
 * 构造芯烨打印机所需的数据格式 (交接班账单专用)
 * @param {Object} rawData 交接班详情数据
 * @param {String} printerId 打印机ID/SN
 * @returns {Object} 供 nativePlugin.xinYe.printText 使用的参数对象
 */
export const buildHandoverPrintData58 = (rawData, printerId) => {
    const dataList = [];

    // --- 内部快捷辅助生成器 ---
    const addText = (text, size = 0, align = 'LEFT', bold = false) => {
        dataList.push({ type: 'text', content: text, flex: '1', size, align, bold });
    };

    const addDashedLine = (title) => {
        if (title.length > 4) {
            addText(`\n----------${title}----------\n`, 0, 'CENTER');
        } else {
            addText(`\n------------${title}------------\n`, 0, 'CENTER');
        }
    };

    const addKeyValueTable = (items) => {
        if (!items || items.length === 0) return;
        const tTitles = [
            { content: items[0][0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 22 },
            { content: items[0][1], flex: '1', size: 0, align: 'RIGHT', bold: false, width: 18 }
        ];
        const tRows = [];
        for (let i = 1; i < items.length; i++) {
            tRows.push({ content: items[i][0] });
            tRows.push({ content: items[i][1] });
        }
        dataList.push({ type: 'table', tableTitles: tTitles, tableRows: tRows });
    };

    // ================= 【1】 头部信息 =================
    addText(`班次：${rawData?.shift?.shift_no || '--'}`);
    addText(`接班时间：${rawData?.shift?.start_at || '--'}`);
    addText(`交班时间：${rawData?.shift?.handover_at || '--'}`);
    addText(`交班人：${rawData?.shift?.user_name || '--'}`);

    // ================= 【2】 现金对账 =================
    addDashedLine('现金对账');
    addKeyValueTable([['现金池', '￥' + String(rawData?.shift?.cash_pool_total || '0')]]);
    addKeyValueTable([['交班现金', '￥' + String(rawData?.shift?.actual_cash || '0')]]);

    // ================= 【3】 支付明细 =================
    if (rawData?.payment_detail && rawData?.payment_detail?.length > 0) {
        addDashedLine('支付明细');
        const payItems = rawData?.payment_detail?.map((item) => [item.name, '￥' + String(item.amount)]);
        addKeyValueTable(payItems);
    }

    // ================= 【4】 退款明细 =================
    if (rawData?.refund_detail && rawData?.refund_detail?.length > 0) {
        addDashedLine('退款明细');
        const refundItems = rawData?.refund_detail.map((item) => [item.name, '￥' + String(item.amount)]);
        addKeyValueTable(refundItems);
    }

    // ================= 【5】 现金变动记录 (采用隐形表头对齐方案) =================
    if (rawData?.cash_changes && rawData?.cash_changes?.length > 0) {
        addDashedLine('现金变动记录');

        rawData?.cash_changes.forEach((item) => {
            // 第一行：操作人 (左边) 和 金额 (右边)
            const row1Titles = [
                { content: item.operator_name || '--', flex: '1', size: 0, align: 'LEFT', bold: false, width: 22 },
                { content: '￥' + String(item.change_amount || 0), flex: '1', size: 0, align: 'RIGHT', bold: false, width: 18 }
            ];
            // tableRows 留空，强行触发 SDK 表头渲染引擎
            dataList.push({ type: 'table', tableTitles: row1Titles, tableRows: [] });

            // 第二行：时间 (左边) 和 备注 (右边)
            // 给右边的备注分配更大的 width (20)，让它在右侧有足够的空间安全换行
            const row2Titles = [
                { content: item.created_at ? item.created_at.substring(5) : '--', flex: '1', size: 0, align: 'LEFT', bold: false, width: 22 },
                { content: item.remark || '', flex: '1', size: 0, align: 'RIGHT', bold: false, width: 18 }
            ];
            dataList.push({ type: 'table', tableTitles: row2Titles, tableRows: [] });
        });
    }

    // 底部留白走纸
    addText(`\n\n\n\n`);

    return {
        titleInfo: {
            title: '交接班小票',
            size: 20
        },
        printerId: printerId,
        data: dataList
    };
};

export const buildHandoverPrintData80 = (rawData, printerId) => {
    const dataList = [];

    // --- 内部快捷辅助生成器 ---
    const addText = (text, size = 0, align = 'LEFT', bold = false) => {
        dataList.push({ type: 'text', content: text, flex: '1', size, align, bold });
    };

    const addDashedLine = (title) => {
        // addText(`\n--------------------------------\n`, 0, 'CENTER');
        if (title.length > 4) {
            addText(`\n-----------------${title}-----------------\n`, 0, 'CENTER');
        } else {
            addText(`\n-------------------${title}-------------------\n`, 0, 'CENTER');
        }
    };

    const addKeyValueTable = (items) => {
        if (!items || items.length === 0) return;
        const tTitles = [
            { content: items[0][0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 32 },
            { content: items[0][1], flex: '1', size: 0, align: 'RIGHT', bold: false, width: 20 }
        ];
        const tRows = [];
        for (let i = 1; i < items.length; i++) {
            tRows.push({ content: items[i][0] });
            tRows.push({ content: items[i][1] });
        }
        dataList.push({ type: 'table', tableTitles: tTitles, tableRows: tRows });
    };

    // ================= 【1】 头部信息 =================
    // addText(`交接班小票\n\n`, 1, 'CENTER', true);
    addText(`班次：${rawData?.shift?.shift_no || '--'}`);
    addText(`接班时间：${rawData?.shift?.start_at || '--'}`);
    addText(`交班时间：${rawData?.shift?.handover_at || '--'}`);
    addText(`交班人：${rawData?.shift?.user_name || '--'}`);
    // addDashedLine();

    // ================= 【2】 现金对账 =================
    // addText(`\n现金对账\n\n`, 0, 'CENTER', false);
    addDashedLine('现金对账');
    addKeyValueTable([['现金池', '￥' + String(rawData?.shift?.cash_pool_total || '0')]]);
    addKeyValueTable([['交班现金', '￥' + String(rawData?.shift?.actual_cash || '0')]]);

    // ================= 【3】 支付明细 =================
    // addText(`\n支付明细\n\n`, 0, 'CENTER', false);
    if (rawData?.payment_detail && rawData?.payment_detail?.length > 0) {
        addDashedLine('支付明细');
        const payItems = rawData?.payment_detail?.map((item) => [item.name, '￥' + String(item.amount)]);
        addKeyValueTable(payItems);
    }

    // ================= 【4】 退款明细 =================
    // addText(`\n退款明细\n\n`, 0, 'CENTER', false);
    if (rawData?.refund_detail && rawData?.refund_detail?.length > 0) {
        addDashedLine('退款明细');
        const refundItems = rawData?.refund_detail.map((item) => [item.name, '￥' + String(item.amount)]);
        addKeyValueTable(refundItems);
    }

    // ================= 【5】 现金变动明细 =================
    if (rawData?.cash_changes && rawData?.cash_changes?.length > 0) {
        addDashedLine('现金变动记录');

        rawData?.cash_changes.forEach((item) => {
            // 第一行：操作人 (左边) 和 金额 (右边)
            const row1Titles = [
                { content: item.operator_name || '--', flex: '1', size: 0, align: 'LEFT', bold: false, width: 32 },
                { content: '￥' + String(item.change_amount || 0), flex: '1', size: 0, align: 'RIGHT', bold: false, width: 20 }
            ];
            // tableRows 留空，强行触发 SDK 表头渲染引擎
            dataList.push({ type: 'table', tableTitles: row1Titles, tableRows: [] });

            // 第二行：时间 (左边) 和 备注 (右边)
            // 给右边的备注分配更大的 width (20)，让它在右侧有足够的空间安全换行
            const row2Titles = [
                { content: item.created_at ? item.created_at.substring(5) : '--', flex: '1', size: 0, align: 'LEFT', bold: false, width: 32 },
                { content: item.remark || '', flex: '1', size: 0, align: 'RIGHT', bold: false, width: 20 }
            ];
            dataList.push({ type: 'table', tableTitles: row2Titles, tableRows: [] });
        });
    }

    // 底部留白走纸
    addText(`\n\n\n\n`);

    return {
        titleInfo: {
            title: '交接班小票',
            size: 20
        },
        printerId: printerId,
        data: dataList
    };
};

//日切小票
/**
 * 构造芯烨打印机所需的数据格式 (日切小票专用)
 * @param {Object} rawData 后端返回的日切详情数据
 * @param {String} printerId 打印机ID/SN
 * @returns {Object} 供 nativePlugin.xinYe.printText 使用的参数对象
 */
export const buildDailyCutPrintData58 = (rawData, printerId) => {
    const dataList = [];

    // 内部文本构造器
    const addText = (text, size = 0, align = 'LEFT', bold = false) => {
        dataList.push({ type: 'text', content: text, flex: '1', size, align, bold });
    };

    const addDashedLine = (title) => {
        if (title.length > 4) {
            addText(`\n----------${title}----------\n`, 0, 'CENTER');
        } else {
            addText(`\n------------${title}------------\n`, 0, 'CENTER');
        }
    };

    const addTwoColTable = (items) => {
        if (!items || items.length === 0) return;
        const tTitles = [
            { content: items[0][0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 20 },
            { content: items[0][1], flex: '1', size: 0, align: 'RIGHT', bold: false, width: 18 }
        ];
        const tRows = [];
        for (let i = 1; i < items.length; i++) {
            tRows.push({ content: items[i][0] });
            tRows.push({ content: items[i][1] });
        }
        dataList.push({ type: 'table', tableTitles: tTitles, tableRows: tRows });
    };

    // 辅助构造器：三列表格 (总列宽 14+8+10=32)
    const addThreeColTable = (headers, items) => {
        if (!items || items.length === 0) return;
        const tTitles = [
            { content: headers[0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 12 },
            { content: headers[1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 12 },
            { content: headers[2], flex: '1', size: 0, align: 'RIGHT', bold: false, width: 10 }
        ];
        const tRows = [];
        items.forEach((row) => {
            tRows.push({ content: String(row[0] || '') });
            tRows.push({ content: String(row[1] || '') });
            tRows.push({ content: String(row[2] || '') });
        });
        dataList.push({ type: 'table', tableTitles: tTitles, tableRows: tRows });
    };

    // ================= 【1】 头部信息 =================
    // addText(`日切小票\n\n`, 1, 'CENTER', true);
    addText(`开始时间：${rawData?.report_date || '--'}`);
    addText(`结束时间：${rawData?.report_date_end || '--'}`);
    // addDashedLine();

    // ================= 【2】 营业概况 =================
    if (rawData?.total_sales) {
        // addText(`\n营业概况\n\n`, 0, 'CENTER', false);
        addDashedLine('营业概况');
        let List = [
            { label: '客单总数', value: rawData?.total_bills },
            { label: '总营业额', value: '￥' + rawData?.total_sales },
            { label: '会员充值金额', value: '￥' + rawData?.total_charge },
            { label: '退款总额', value: '￥' + rawData?.total_refund }
        ];
        const overviewItems = List?.map((item) => [item.label, String(item.value)]);
        // 插入第一行作为隐形表头占位结构
        overviewItems.unshift([overviewItems[0][0], overviewItems[0][1]]);
        addTwoColTable(overviewItems);
        // addDashedLine();
    }

    // ================= 【3】 客单详情 =================
    if (rawData.income_detail && rawData.income_detail.length > 0) {
        // addText(`\n客单详情\n\n`, 0, 'CENTER', false);
        addDashedLine('客单详情');
        const orderItems = rawData.income_detail.map((item) => [item.name, '￥' + String(item.amount)]);
        orderItems.unshift([orderItems[0][0], orderItems[0][1]]);
        addTwoColTable(orderItems);
    }

    // ================= 【4】 优惠明细 =================
    if (rawData.discount_summary && rawData.discount_summary.length > 0) {
        addDashedLine('优惠明细');
        const discountItems = rawData.discount_summary.map((item) => [item.name, '￥' + String(item.amount)]);
        discountItems.unshift([discountItems[0][0], discountItems[0][1]]);
        addTwoColTable(discountItems);
    }
    // ================= 【5】 支付明细 =================
    if (rawData.payment_summary && rawData.payment_summary.length > 0) {
        addDashedLine('支付明细');
        const discountItems = rawData.payment_summary.map((item) => [item.pay_method_name, '￥' + String(item.amount)]);
        discountItems.unshift([discountItems[0][0], discountItems[0][1]]);
        addTwoColTable(discountItems);
    }
    // ================= 【6】 退款明细 =================
    if (rawData.refund_summary && rawData.refund_summary.length > 0) {
        addDashedLine('退款明细');
        const discountItems = rawData.refund_summary.map((item) => [item.name, '￥' + String(item.amount)]);
        discountItems.unshift([discountItems[0][0], discountItems[0][1]]);
        addTwoColTable(discountItems);
    }
    // ================= 【7】 桌台明细 (双列结构) =================
    if (rawData.table_detail && rawData.table_detail.length > 0) {
        addDashedLine('桌台明细');
        const tableItems = [['区域', '金额']];
        rawData.table_detail.forEach((item) => {
            tableItems.push([item.name, '￥' + String(item.amount)]);
        });
        addTwoColTable(tableItems);
    }

    // ================= 【8】 商品销售明细 (三列结构) =================
    if (rawData.goods_sales && rawData.goods_sales.length > 0) {
        addDashedLine('商品销售明细');
        const items = rawData.goods_sales.map((i) => [i.name, i.quantity, '￥' + i.amount]);
        addThreeColTable(['名称', '数量', '金额'], items);
    }

    // ================= 【9】 餐食销售明细 (三列结构) =================
    if (rawData.food_sales && rawData.food_sales.length > 0) {
        addDashedLine('餐食销售明细');
        const items = rawData.food_sales.map((i) => [i.name, i.quantity, '￥' + i.amount]);
        addThreeColTable(['名称', '数量', '金额'], items);
    }

    // ================= 【10】 教练明细 (三列结构) =================
    if (rawData.coach_detail && rawData.coach_detail.length > 0) {
        addDashedLine('教练明细');
        const items = rawData.coach_detail.map((i) => [i.name, i.unit, '￥' + i.amount]);
        addThreeColTable(['名称', '单位', '金额'], items);
    }

    // ================= 【11】 礼物明细 (三列结构) =================
    if (rawData.gift_detail && rawData.gift_detail.length > 0) {
        addDashedLine('礼物明细');
        const items = rawData.gift_detail.map((i) => [i.name, i.count, '￥' + i.amount]);
        addThreeColTable(['名称', '数量', '金额'], items);
    }

    // ================= 【12】 充电明细 (三列结构) =================
    // if (rawData.charge_details && rawData.charge_details.length > 0) {
    //     addText(`\n充电明细\n\n`, 0, 'CENTER', false);
    //     const items = rawData.charge_details.map(i => [i.name, i.duration, i.amount]);
    //     addThreeColTable(['名称', '时长', '金额'], items);
    //     addDashedLine();
    // }

    // 底部留白走纸
    addText(`\n\n\n`);

    return {
        titleInfo: {
            title: '日切小票',
            size: 20
        },
        printerId: printerId,
        data: dataList
    };
};

export const buildDailyCutPrintData80 = (rawData, printerId) => {
    const dataList = [];

    // 内部文本构造器
    const addText = (text, size = 0, align = 'LEFT', bold = false) => {
        dataList.push({ type: 'text', content: text, flex: '1', size, align, bold });
    };

    const addDashedLine = (title) => {
        if (title.length > 4) {
            addText(`\n-----------------${title}-----------------\n`, 0, 'CENTER');
        } else {
            addText(`\n-------------------${title}-------------------\n`, 0, 'CENTER');
        }
    };

    const addTwoColTable = (items) => {
        if (!items || items.length === 0) return;
        const tTitles = [
            { content: items[0][0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 35 },
            { content: items[0][1], flex: '1', size: 0, align: 'RIGHT', bold: false, width: 20 }
        ];
        const tRows = [];
        for (let i = 1; i < items.length; i++) {
            tRows.push({ content: items[i][0] });
            tRows.push({ content: items[i][1] });
        }
        dataList.push({ type: 'table', tableTitles: tTitles, tableRows: tRows });
    };

    // 辅助构造器：三列表格 (总列宽)
    const addThreeColTable = (headers, items) => {
        if (!items || items.length === 0) return;
        const tTitles = [
            { content: headers[0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 20 },
            { content: headers[1], flex: '1', size: 0, align: 'LEFT', bold: false, width: 18 },
            { content: headers[2], flex: '1', size: 0, align: 'LEFT', bold: false, width: 22 }
        ];
        const tRows = [];
        items.forEach((row) => {
            tRows.push({ content: String(row[0] || '') });
            tRows.push({ content: String(row[1] || '') });
            tRows.push({ content: String(row[2] || '') });
        });
        dataList.push({ type: 'table', tableTitles: tTitles, tableRows: tRows });
    };

    // ================= 【1】 头部信息 =================
    // addText(`日切小票\n\n`, 1, 'CENTER', true);
    addText(`开始时间：${rawData?.report_date || '--'}`);
    addText(`结束时间：${rawData?.report_date_end || '--'}`);
    // addDashedLine();

    // ================= 【2】 营业概况 =================
    if (rawData?.total_sales) {
        // addText(`\n营业概况\n\n`, 0, 'CENTER', false);
        addDashedLine('营业概况');
        let List = [
            { label: '客单总数', value: rawData?.total_bills },
            { label: '总营业额', value: '￥' + rawData?.total_sales },
            { label: '会员充值金额', value: '￥' + rawData?.total_charge },
            { label: '退款总额', value: '￥' + rawData?.total_refund }
        ];
        const overviewItems = List?.map((item) => [item.label, String(item.value)]);
        // 插入第一行作为隐形表头占位结构
        overviewItems.unshift([overviewItems[0][0], overviewItems[0][1]]);
        addTwoColTable(overviewItems);
        // addDashedLine();
    }

    // ================= 【3】 客单详情 =================
    if (rawData.income_detail && rawData.income_detail.length > 0) {
        // addText(`\n客单详情\n\n`, 0, 'CENTER', false);
        addDashedLine('客单详情');
        const orderItems = rawData.income_detail.map((item) => [item.name, '￥' + String(item.amount)]);
        orderItems.unshift([orderItems[0][0], orderItems[0][1]]);
        addTwoColTable(orderItems);
    }

    // ================= 【4】 优惠明细 =================
    if (rawData.discount_summary && rawData.discount_summary.length > 0) {
        addDashedLine('优惠明细');
        const discountItems = rawData.discount_summary.map((item) => [item.name, '￥' + String(item.amount)]);
        discountItems.unshift([discountItems[0][0], discountItems[0][1]]);
        addTwoColTable(discountItems);
    }
    // ================= 【5】 支付明细 =================
    if (rawData.payment_summary && rawData.payment_summary.length > 0) {
        addDashedLine('支付明细');
        const discountItems = rawData.payment_summary.map((item) => [item.pay_method_name, '￥' + String(item.amount)]);
        discountItems.unshift([discountItems[0][0], discountItems[0][1]]);
        addTwoColTable(discountItems);
    }
    // ================= 【6】 退款明细 =================
    if (rawData.refund_summary && rawData.refund_summary.length > 0) {
        addDashedLine('退款明细');
        const discountItems = rawData.refund_summary.map((item) => [item.name, '￥' + String(item.amount)]);
        discountItems.unshift([discountItems[0][0], discountItems[0][1]]);
        addTwoColTable(discountItems);
    }
    // ================= 【7】 桌台明细 (双列结构) =================
    if (rawData.table_detail && rawData.table_detail.length > 0) {
        addDashedLine('桌台明细');
        const tableItems = [['区域', '金额']];
        rawData.table_detail.forEach((item) => {
            tableItems.push([item.name, String(item.amount)]);
        });
        addTwoColTable(tableItems);
    }

    // ================= 【8】 商品销售明细 (三列结构) =================
    if (rawData.goods_sales && rawData.goods_sales.length > 0) {
        addDashedLine('商品销售明细');
        const items = rawData.goods_sales.map((i) => [i.name, i.quantity, '￥' + i.amount]);
        addThreeColTable(['名称', '数量', '金额'], items);
    }

    // ================= 【9】 餐食销售明细 (三列结构) =================
    if (rawData.food_sales && rawData.food_sales.length > 0) {
        addDashedLine('餐食销售明细');
        const items = rawData.food_sales.map((i) => [i.name, i.quantity, '￥' + i.amount]);
        addThreeColTable(['名称', '数量', '金额'], items);
    }

    // ================= 【10】 教练明细 (三列结构) =================
    if (rawData.coach_detail && rawData.coach_detail.length > 0) {
        addDashedLine('教练明细');
        const items = rawData.coach_detail.map((i) => [i.name, i.unit, '￥' + i.amount]);
        addThreeColTable(['名称', '单位', '金额'], items);
    }

    // ================= 【11】 礼物明细 (三列结构) =================
    if (rawData.gift_detail && rawData.gift_detail.length > 0) {
        addDashedLine('礼物明细');
        const items = rawData.gift_detail.map((i) => [i.name, i.count, '￥' + i.amount]);
        addThreeColTable(['名称', '数量', '金额'], items);
    }

    // ================= 【12】 充电明细 (三列结构) =================
    // if (rawData.charge_details && rawData.charge_details.length > 0) {
    //     addText(`\n充电明细\n\n`, 0, 'CENTER', false);
    //     const items = rawData.charge_details.map(i => [i.name, i.duration, i.amount]);
    //     addThreeColTable(['名称', '时长', '金额'], items);
    //     addDashedLine();
    // }

    // 底部留白走纸
    addText(`\n\n\n`);

    return {
        titleInfo: {
            title: '日切小票',
            size: 20
        },
        printerId: printerId,
        data: dataList
    };
};

//餐食/商品下单小票
/**
 * 构造芯烨打印机所需的数据格式 (仅商品/餐食专用小票)
 * @param {Object} rawData 后端返回的详情数据
 * @param {String} printerId 打印机ID/SN
 * @param {Object} shopInfo 店铺信息
 * @returns {Object} 供 nativePlugin.xinYe.printText 使用的参数对象
 */
export const buildGoodsMealsPrintData58 = (rawData, printerId, shopInfo = {}) => {
    const dataList = [];

    // --- 内部快捷辅助生成器 ---
    const addText = (text, size = 0, align = 'LEFT', bold = false) => {
        dataList.push({ type: 'text', content: text, flex: '1', size, align, bold });
    };

    const addDashedLine = (title = '') => {
        if (title) {
            addText(`\n------------ ${title} ------------\n`, 0, 'CENTER');
        } else {
            addText(`\n--------------------------------\n`, 0, 'CENTER');
        }
    };

    // 隐形表头：专为名称+数量的 2 列排版设计（保证 不溢出）
    const addTwoColumnTable = (items) => {
        if (!items || items.length === 0) return;
        const tTitles = [
            { content: items[0][0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 26 },
            { content: items[0][1], flex: '1', size: 0, align: 'RIGHT', bold: false, width: 10 }
        ];
        const tRows = [];
        for (let i = 1; i < items.length; i++) {
            tRows.push({ content: items[i][0] });
            tRows.push({ content: items[i][1] });
        }
        dataList.push({ type: 'table', tableTitles: tTitles, tableRows: tRows });
    };

    // ================= 【1】 头部信息 =================
    if (rawData.table_name) {
        addText(`桌台号：${rawData.table_name}`);
    }
    if (rawData.name) {
        addText(`下单人：${rawData.name}`);
    }
    if (rawData.order_time) {
        addText(`下单时间：${rawData.order_time}`);
    }

    // ================= 【2】 商品模块 =================
    if (rawData.goods && rawData.goods.length > 0) {
        addDashedLine('商品');
        const goodsItems = [['名称', '数量']];
        rawData.goods.forEach((item) => {
            let name=item?.goods?.sku_name?(item?.goods?.goods_name+','+item?.goods?.sku_name):item?.goods?.goods_name
            goodsItems.push([name || '--', String(item?.goods?.num || 0)]);
        });
        addTwoColumnTable(goodsItems);
    }

    // ================= 【3】 餐食模块 =================
    if (rawData.meals && rawData.meals.length > 0) {
        addDashedLine('餐食');
        const mealItems = [['名称', '数量']];
        rawData.meals.forEach((item) => {
            let name=item?.goods?.sku_name?(item?.goods?.goods_name+','+item?.goods?.sku_name):item?.goods?.goods_name
            mealItems.push([name || '--', String(item?.goods?.num || 0)]);
        });
        addTwoColumnTable(mealItems);
    }

    // 底部留白走纸
    addText(`\n\n\n\n`);

    // 最终返回符合 SDK 要求的对象
    return {
        titleInfo: {
            title: shopInfo.name || '店铺名称',
            size: 20
        },
        printerId: printerId,
        data: dataList
    };
};

export const buildGoodsMealsPrintData80 = (rawData, printerId, shopInfo = {}) => {
    const dataList = [];

    // --- 内部快捷辅助生成器 ---
    const addText = (text, size = 0, align = 'LEFT', bold = false) => {
        dataList.push({ type: 'text', content: text, flex: '1', size, align, bold });
    };

    const addDashedLine = (title = '') => {
        if (title) {
            if (title?.length > 2) {
                addText(`\n------------------${title}------------------\n`, 0, 'CENTER');
            } else {
                addText(`\n---------------------${title}---------------------\n`, 0, 'CENTER');
            }
        } else {
            addText(`\n--------------------------------------------\n`, 0, 'CENTER');
        }
    };

    // 隐形表头：专为名称+数量的 2 列排版设计（保证 不溢出）
    const addTwoColumnTable = (items) => {
        if (!items || items.length === 0) return;
        const tTitles = [
            { content: items[0][0], flex: '1', size: 0, align: 'LEFT', bold: false, width: 38 },
            { content: items[0][1], flex: '1', size: 0, align: 'RIGHT', bold: false, width: 18 }
        ];
        const tRows = [];
        for (let i = 1; i < items.length; i++) {
            tRows.push({ content: items[i][0] });
            tRows.push({ content: items[i][1] });
        }
        dataList.push({ type: 'table', tableTitles: tTitles, tableRows: tRows });
    };

    // ================= 【1】 头部信息 =================
    if (rawData.table_name) {
        addText(`桌台号：${rawData.table_name}`);
    }
    if (rawData.name) {
        addText(`下单人：${rawData.name}`);
    }
    if (rawData.order_time) {
        addText(`下单时间：${rawData.order_time}`);
    }

    // ================= 【2】 商品模块 =================
    if (rawData.goods && rawData.goods.length > 0) {
        addDashedLine('商品');
        const goodsItems = [['名称', '数量']];
        rawData.goods.forEach((item) => {
            let name=item?.goods?.sku_name?(item?.goods?.goods_name+','+item?.goods?.sku_name):item?.goods?.goods_name
            goodsItems.push([name || '--', String(item?.goods?.num || 0)]);
        });
        addTwoColumnTable(goodsItems);
    }

    // ================= 【3】 餐食模块 =================
    if (rawData.meals && rawData.meals.length > 0) {
        addDashedLine('餐食');
        const mealItems = [['名称', '数量']];
        rawData.meals.forEach((item) => {
            let name=item?.goods?.sku_name?(item?.goods?.goods_name+','+item?.goods?.sku_name):item?.goods?.goods_name
            mealItems.push([name || '--', String(item?.goods?.num || 0)]);
        });
        addTwoColumnTable(mealItems);
    }

    // 底部留白走纸
    addText(`\n\n\n\n`);

    // 最终返回符合 SDK 要求的对象
    return {
        titleInfo: {
            title: shopInfo.name || '店铺名称',
            size: 20
        },
        printerId: printerId,
        data: dataList
    };
};